// Floating toolbar (bubble menu) for inline text formatting.
// Appears when text is selected in the TipTap editor.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { AnchoredMenu } from '@/components/AnchoredMenu';

interface FloatingToolbarProps {
  editor: Editor;
}

export function FloatingToolbar({ editor }: FloatingToolbarProps) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Track selection changes to show/hide toolbar
  useEffect(() => {
    const updateToolbar = () => {
      const { selection } = editor.state;
      const { empty } = selection;

      if (empty) {
        setVisible(false);
        setShowLinkInput(false);
        return;
      }

      // Get bounding rect of selection
      const { view } = editor;
      const { from, to } = selection;
      const start = view.coordsAtPos(from);
      const end = view.coordsAtPos(to);

      const editorEl = view.dom.closest('.document-editor');
      if (!editorEl) return;

      const editorRect = editorEl.getBoundingClientRect();
      const toolbarHeight = 40;

      setPosition({
        top: start.top - editorRect.top - toolbarHeight - 8,
        left: (start.left + end.left) / 2 - editorRect.left,
      });
      setVisible(true);
    };

    editor.on('selectionUpdate', updateToolbar);
    editor.on('transaction', updateToolbar);

    return () => {
      editor.off('selectionUpdate', updateToolbar);
      editor.off('transaction', updateToolbar);
    };
  }, [editor]);

  const toggleBold = useCallback(() => editor.chain().focus().toggleBold().run(), [editor]);
  const toggleItalic = useCallback(() => editor.chain().focus().toggleItalic().run(), [editor]);
  const toggleUnderline = useCallback(() => editor.chain().focus().toggleUnderline().run(), [editor]);
  const toggleStrike = useCallback(() => editor.chain().focus().toggleStrike().run(), [editor]);
  const toggleCode = useCallback(() => editor.chain().focus().toggleCode().run(), [editor]);

  const handleLinkSubmit = useCallback(() => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const openLinkInput = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href || '';
    setLinkUrl(previousUrl);
    setShowLinkInput(true);
  }, [editor]);

  if (!visible) return null;

  return (
    <div
      ref={toolbarRef}
      className="absolute z-50 flex items-center gap-0.5 rounded-lg border border-border bg-popover p-1 shadow-lg"
      style={{
        top: position.top,
        left: position.left,
        transform: 'translateX(-50%)',
      }}
    >
      {showLinkInput ? (
        <div className="flex items-center gap-1 px-1">
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleLinkSubmit();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setShowLinkInput(false);
              }
            }}
            placeholder="Paste link..."
            className="w-48 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/50"
            autoFocus
          />
          <ToolbarButton onClick={handleLinkSubmit} title="Apply">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </ToolbarButton>
        </div>
      ) : (
        <>
          <ToolbarButton
            onClick={toggleBold}
            isActive={editor.isActive('bold')}
            title="Bold (Ctrl+B)"
          >
            <span className="font-bold text-sm">B</span>
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleItalic}
            isActive={editor.isActive('italic')}
            title="Italic (Ctrl+I)"
          >
            <span className="italic text-sm">I</span>
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleUnderline}
            isActive={editor.isActive('underline')}
            title="Underline (Ctrl+U)"
          >
            <span className="underline text-sm">U</span>
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleStrike}
            isActive={editor.isActive('strike')}
            title="Strikethrough"
          >
            <span className="line-through text-sm">S</span>
          </ToolbarButton>

          <div className="w-px h-5 bg-border mx-0.5" />

          <ToolbarButton
            onClick={toggleCode}
            isActive={editor.isActive('code')}
            title="Inline code"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
          </ToolbarButton>
          <ToolbarButton
            onClick={openLinkInput}
            isActive={editor.isActive('link')}
            title="Link"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </ToolbarButton>

          <div className="w-px h-5 bg-border mx-0.5" />

          <ColorPicker
            editor={editor}
            type="text"
            title="Text color"
          />
          <ColorPicker
            editor={editor}
            type="highlight"
            title="Highlight"
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ToolbarButton({
  onClick,
  isActive,
  title,
  children,
}: {
  onClick: () => void;
  isActive?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${
        isActive
          ? 'bg-accent text-accent-foreground'
          : 'text-foreground hover:bg-accent/50'
      }`}
    >
      {children}
    </button>
  );
}

const COLORS = [
  { label: 'Default', value: '' },
  { label: 'Red', value: '#ef4444' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Yellow', value: '#eab308' },
  { label: 'Green', value: '#22c55e' },
  { label: 'Blue', value: '#3b82f6' },
  { label: 'Purple', value: '#a855f7' },
  { label: 'Pink', value: '#ec4899' },
];

function ColorPicker({
  editor,
  type,
  title,
}: {
  editor: Editor;
  type: 'text' | 'highlight';
  title: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const applyColor = useCallback(
    (color: string) => {
      if (type === 'text') {
        if (color) {
          editor.chain().focus().setColor(color).run();
        } else {
          editor.chain().focus().unsetColor().run();
        }
      } else {
        if (color) {
          editor.chain().focus().setHighlight({ color }).run();
        } else {
          editor.chain().focus().unsetHighlight().run();
        }
      }
      setOpen(false);
    },
    [editor, type],
  );

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        title={title}
        className="w-7 h-7 flex items-center justify-center rounded text-foreground hover:bg-accent/50 transition-colors"
      >
        {type === 'text' ? (
          <span className="text-sm font-bold" style={{ color: '#3b82f6' }}>A</span>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#eab308" stroke="currentColor" strokeWidth="1">
            <rect x="3" y="3" width="18" height="18" rx="2" />
          </svg>
        )}
      </button>
      <AnchoredMenu
        anchor={triggerRef}
        open={open}
        onClose={() => setOpen(false)}
        role="none"
        className="flex gap-1 p-1.5"
      >
          {COLORS.map((c) => (
            <button
              key={c.value || 'default'}
              type="button"
              onClick={() => applyColor(c.value)}
              title={c.label}
              className="w-5 h-5 rounded-sm border border-border/50 hover:scale-110 transition-transform"
              style={{
                backgroundColor: type === 'highlight'
                  ? (c.value || 'transparent')
                  : 'transparent',
                color: type === 'text' ? (c.value || 'currentColor') : undefined,
              }}
            >
              {type === 'text' && <span className="text-xs font-bold">A</span>}
            </button>
          ))}
      </AnchoredMenu>
    </div>
  );
}
